import React, { useState } from 'react'
import { Users, Mail, QrCode, Check, Loader2 } from 'lucide-react'
import type { ContactMethodProps } from '@/features/enrollment'

export default function ContactMethod({
  enrollmentData,
  updateEnrollmentData,
  generateQRCode,
  sendEmailLink,
  sessionId
}: ContactMethodProps) {
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const handleSendEmailLink = async () => {
    if (!enrollmentData.clientEmail) return
    
    setIsSendingEmail(true)
    setEmailSent(false)
    try {
      await sendEmailLink()
      setEmailSent(true)
      // Hide success message after 3 seconds
      setTimeout(() => setEmailSent(false), 3000)
    } catch (error) {
      console.error('Failed to send email link:', error)
    } finally {
      setIsSendingEmail(false)
    }
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
          <Users className="w-5 h-5 sm:w-6 sm:h-6 text-secondary-600" />
        </div>
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-accent-900">Client Contact Method</h3>
          <p className="text-xs sm:text-sm text-accent-600">Choose how to send the enrollment form to your client</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Option 1 - Send Client Link */}
        <div className="border-2 border-accent-200 rounded-lg p-3 sm:p-4 lg:p-6 hover:border-primary-300 transition-colors">
          <div className="text-center">
            <button
              className={`w-full h-20 sm:h-24 lg:h-32 rounded-lg mb-3 sm:mb-4 flex items-center justify-center transition-colors ${
                enrollmentData.clientContactMethod === 'email' ? 'bg-primary-600 text-white' : 'bg-primary-100 hover:bg-primary-200 text-primary-700'
              }`}
              onClick={() => updateEnrollmentData('clientContactMethod', 'email')}
            >
              <div>
                <Users className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 mx-auto mb-1 sm:mb-2" />
                <div className="font-semibold text-xs sm:text-sm lg:text-base">Send Client Link</div>
              </div>
            </button>
            <h4 className="font-semibold text-sm sm:text-base text-accent-900 mb-1 sm:mb-2">Option 1: Email Link</h4>
            <div className="text-xs sm:text-sm text-accent-600 space-y-1">
              <p>• Open email dialog</p>
              <p>• Enter client email address</p>
              <p>• Send enrollment link directly</p>
              <p>• Client receives Step 1 form</p>
            </div>
          </div>
        </div>

        {/* Option 2 - Client Scan QR */}
        <div className="border-2 border-accent-200 rounded-lg p-3 sm:p-4 lg:p-6 hover:border-primary-300 transition-colors">
          <div className="text-center">
            <button
              className={`w-full h-20 sm:h-24 lg:h-32 rounded-lg mb-3 sm:mb-4 flex items-center justify-center transition-colors ${
                enrollmentData.clientContactMethod === 'qr' ? 'bg-secondary-600 text-white' : 'bg-secondary-100 hover:bg-secondary-200 text-secondary-700'
              }`}
              onClick={() => updateEnrollmentData('clientContactMethod', 'qr')}
            >
              <div>
                <QrCode className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 mx-auto mb-1 sm:mb-2" />
                <div className="font-semibold text-xs sm:text-sm lg:text-base">QR Code Scan</div>
              </div>
            </button>
            <h4 className="font-semibold text-sm sm:text-base text-accent-900 mb-1 sm:mb-2">Option 2: QR Code</h4>
            <div className="text-xs sm:text-sm text-accent-600 space-y-1">
              <p>• Generate QR code popup</p>
              <p>• Client scans with phone</p>
              <p>• Direct access to Step 1 form</p>
              <p>• No email required</p>
            </div>
          </div>
        </div>
      </div>

      {enrollmentData.clientContactMethod === 'email' && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <label className="block text-xs sm:text-sm font-medium text-primary-900 mb-1 sm:mb-2">Client Email Address</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary-400 w-4 h-4" />
            <input
              type="email"
              className="input-field pl-10 text-sm sm:text-base"
              placeholder="Enter client's email address"
              value={enrollmentData.clientEmail}
              onChange={(e) => updateEnrollmentData('clientEmail', e.target.value)}
            />
          </div>
          <div className="mt-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
            <button 
              className="btn-primary w-full sm:w-auto flex items-center justify-center space-x-2" 
              onClick={handleSendEmailLink} 
              disabled={!enrollmentData.clientEmail || isSendingEmail}
            >
              {isSendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : emailSent ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Mail sent</span>
                </>
              ) : (
                <span>Send Link</span>
              )}
            </button>
            {!(enrollmentData.emailLinkVerified || enrollmentData.identityConsentAgreed) && (
              <span className="text-xs text-primary-700">Waiting for verification...</span>
            )}
            {(enrollmentData.emailLinkVerified || enrollmentData.identityConsentAgreed) && (
              <span className="text-xs text-green-700 flex items-center space-x-1"><Check className="w-3 h-3" /><span>Verified</span></span>
            )}
          </div>
          {sessionId && (
            <div className="mt-3 text-xs text-accent-700">
              <div className="mb-1">Or share identity verification form:</div>
              <div className="flex items-center space-x-2">
                <input className="input-field text-xs" readOnly value={`${(process.env.REACT_APP_API_URL || 'http://localhost:3001/api')}/enrollments/public/sessions/${sessionId}/identity`} />
                <button
                  className="btn-outline"
                  onClick={() => navigator.clipboard.writeText(`${(process.env.REACT_APP_API_URL || 'http://localhost:3001/api')}/enrollments/public/sessions/${sessionId}/identity`)}
                >Copy</button>
              </div>
              {enrollmentData.idDocumentUrl && (
                <div className="mt-2 text-green-700">ID received</div>
              )}
            </div>
          )}
        </div>
      )}

      {enrollmentData.clientContactMethod === 'qr' && (
        <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 text-center">
          <div className="w-32 h-32 sm:w-48 sm:h-48 mx-auto bg-white border border-secondary-200 rounded flex items-center justify-center mb-3 sm:mb-4">
            <QrCode className="w-16 h-16 sm:w-24 sm:h-24 text-secondary-300" />
          </div>
          <p className="text-xs sm:text-sm text-secondary-700 mb-3">Have your client scan this QR code with their phone camera</p>
          <button className="btn-secondary w-full sm:w-auto" onClick={generateQRCode}>
            Generate New QR Code
          </button>
        </div>
      )}
    </div>
  )
}
